import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ActionsService} from '../../shared/footer/actions.service';
import {MobileBackCoordinatorService} from './mobile-back-coordinator.service';
import {MobileExperienceConfigService} from './mobile-experience-config.service';
import {MobilePlatformPolicyService} from './mobile-platform-policy.service';
import {MobileExperienceShellService} from './mobile-experience-shell.service';
import {VideoService} from '../../shared/timeline/video.service';

const WIZARD_TRACE_LIMIT = 200;
const WIZARD_STEPS = ['vehicleType', 'make', 'model', 'bodyType', 'bodyGen', 'edition', 'transmission', 'review'] as const;
type WizardDiagnosticStep = typeof WIZARD_STEPS[number];
const WIZARD_INPUT_EVENTS = ['pointerdown', 'pointerup', 'pointercancel', 'touchstart', 'touchmove', 'touchend', 'touchcancel', 'mousedown', 'mouseup', 'click'] as const;
type WizardPointerInteractionType = 'pointerdown' | 'pointerup' | 'click';
type WizardEventSurface = 'window' | 'document' | 'app' | 'modal' | 'content';
type WizardEventPhase = 'capture' | 'bubble';
type WizardEventMutation = 'preventDefault' | 'stopPropagation' | 'stopImmediatePropagation';
const IONIC_OVERLAY_LIFECYCLE_EVENTS = [
  'ionModalWillPresent', 'ionModalDidPresent', 'ionModalWillDismiss', 'ionModalDidDismiss',
  'ionAlertWillPresent', 'ionAlertDidPresent', 'ionAlertWillDismiss', 'ionAlertDidDismiss',
  'ionActionSheetWillPresent', 'ionActionSheetDidPresent', 'ionActionSheetWillDismiss', 'ionActionSheetDidDismiss',
  'ionPopoverWillPresent', 'ionPopoverDidPresent', 'ionPopoverWillDismiss', 'ionPopoverDidDismiss',
  'ionLoadingWillPresent', 'ionLoadingDidPresent', 'ionLoadingWillDismiss', 'ionLoadingDidDismiss',
  'ionToastWillPresent', 'ionToastDidPresent', 'ionToastWillDismiss', 'ionToastDidDismiss',
] as const;

export interface MobileExperienceDiagnosticsTraceEntry {
  at: number;
  event: string;
  details: Record<string, boolean | number | string | null>;
}

interface WizardPendingTap {
  pointerId: number;
  target: string;
  x: number | null;
  y: number | null;
  at: number;
  clickSeen: boolean;
  timeoutId: number | null;
}

export interface MobileExperienceDiagnosticsSnapshot {
  mobileExperience: boolean;
  mobileShellActive: boolean;
  prefersReducedMotion: boolean;
  mobileShellEnabled: boolean;
  mobileTopNavigationEnabled: boolean;
  mobileVehicleWizardEnabled: boolean;
  mobileFlutterMediaComposerEnabled: boolean;
  mobileEventCreationEnabled: boolean;
  mobileAdvertisementCreationEnabled: boolean;
  mobileCollaborationCreationEnabled: boolean;
  activeActionId: string | null;
  registeredActions: number;
  backHandlers: number;
  dom: {
    videoElements: number;
    ionicOverlays: number;
    activeIonicOverlay: string | null;
    activeIonicOverlayParent: string | null;
    ionicAppRoot: string | null;
    ionicAppRootPointerEvents: string | null;
    conflicts: {
      primeMasks: number;
      bottomSheets: number;
      tutorialActive: boolean;
      htmlClasses: string | null;
      bodyClasses: string | null;
      htmlTouchAction: string | null;
      htmlOverflow: string | null;
      htmlPosition: string | null;
      bodyTouchAction: string | null;
      bodyOverflow: string | null;
      bodyPosition: string | null;
      bodyScrollLocked: boolean;
    };
  };
  video: ReturnType<VideoService['getDiagnostics']>;
  vehicleWizard: {
    open: boolean;
    hasDraft: boolean;
    publishing: boolean;
    step: WizardDiagnosticStep | null;
    loading: boolean;
    transitioning: boolean;
    requestId: number;
    renderedStep: WizardDiagnosticStep | null;
    lastInteraction: {
      type: WizardPointerInteractionType;
      target: string;
      disabled: boolean;
      at: number;
    } | null;
  };
  trace: MobileExperienceDiagnosticsTraceEntry[];
}

type VehicleWizardDiagnosticsUpdate = Omit<MobileExperienceDiagnosticsSnapshot['vehicleWizard'], 'lastInteraction' | 'renderedStep'>
  & Partial<Pick<MobileExperienceDiagnosticsSnapshot['vehicleWizard'], 'lastInteraction'>>;

@Injectable({providedIn: 'root'})
export class MobileExperienceDiagnosticsService implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly isBrowser: boolean;
  private initialized = false;
  private vehicleWizard: MobileExperienceDiagnosticsSnapshot['vehicleWizard'] = {
    open: false,
    hasDraft: false,
    publishing: false,
    step: null,
    loading: false,
    transitioning: false,
    requestId: 0,
    renderedStep: null,
    lastInteraction: null,
  };
  private readonly wizardTrace: MobileExperienceDiagnosticsTraceEntry[] = [];
  private readonly surfaceReleases = new Map<WizardEventSurface, Array<() => void>>();
  private readonly pendingTaps = new Map<number, WizardPendingTap>();
  private readonly patchedEventMethods = new Map<WizardEventMutation, {original: Function; patched: Function}>();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
    private readonly actionsService: ActionsService,
    private readonly mobileBack: MobileBackCoordinatorService,
    private readonly mobileConfig: MobileExperienceConfigService,
    private readonly mobilePlatform: MobilePlatformPolicyService,
    private readonly mobileShell: MobileExperienceShellService,
    private readonly videoService: VideoService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public initialize(): void {
    if (this.initialized || !this.isBrowser || environment.production) {
      return;
    }
    this.initialized = true;
    this.subscriptions.add(this.actionsService.contextualAction$.subscribe(() => this.publish()));
    this.subscriptions.add(this.mobileConfig.config$.subscribe(() => this.publish()));
    this.subscriptions.add(this.mobilePlatform.isMobileExperience$.subscribe(() => this.publish()));
    this.subscriptions.add(this.mobileShell.active$.subscribe(() => this.publish()));
    this.subscriptions.add(this.mobilePlatform.reducedMotion$.subscribe(() => this.publish()));
    WIZARD_INPUT_EVENTS.forEach((eventName) => {
      this.document.addEventListener?.(eventName, this.trackWizardInteractionCapture, true);
      this.document.addEventListener?.(eventName, this.trackWizardInteractionBubble, false);
    });
    this.bindSurface('window', window);
    this.refreshWizardSurfaces();
    this.installEventMutationDiagnostics();
    IONIC_OVERLAY_LIFECYCLE_EVENTS.forEach((eventName) => this.document.addEventListener?.(eventName, this.trackIonicLifecycle, true));
    window.addEventListener('error', this.trackWindowError, true);
    window.addEventListener('unhandledrejection', this.trackUnhandledRejection, true);
    this.publish();
  }

  /**
   * Adds a bounded, development-only breadcrumb without logging visible text,
   * search input, user identifiers, or API payloads.
   */
  public traceVehicleWizard(event: string, details: Record<string, boolean | number | string | null> = {}): void {
    if (!this.initialized || !this.isBrowser || environment.production) {
      return;
    }
    this.appendTrace(event, details);
    this.publish();
  }

  public setVehicleWizardState(state: VehicleWizardDiagnosticsUpdate): void {
    const opened = state.open && !this.vehicleWizard.open;
    this.vehicleWizard = {
      ...this.vehicleWizard,
      ...state,
      lastInteraction: opened ? null : state.lastInteraction ?? this.vehicleWizard.lastInteraction,
    };
    this.publish();
  }

  public snapshot(): MobileExperienceDiagnosticsSnapshot {
    const visibleIonicOverlays = this.visibleIonicOverlays();
    const activeIonicOverlay = visibleIonicOverlays[visibleIonicOverlays.length - 1];
    const appRoot = this.isBrowser ? this.document.getElementById('app-root-content') : null;
    const renderedStep = this.renderedWizardStep();
    return {
      mobileExperience: this.mobilePlatform.isMobileExperience,
      mobileShellActive: this.mobileShell.isActive,
      prefersReducedMotion: this.mobilePlatform.prefersReducedMotion,
      mobileShellEnabled: this.mobileConfig.current.mobileShellEnabled,
      mobileTopNavigationEnabled: this.mobileConfig.current.mobileTopNavigationEnabled,
      mobileVehicleWizardEnabled: this.mobileConfig.current.mobileVehicleWizardEnabled,
      mobileFlutterMediaComposerEnabled: this.mobileConfig.current.mobileFlutterMediaComposerEnabled,
      mobileEventCreationEnabled: this.mobileConfig.current.mobileEventCreationEnabled,
      mobileAdvertisementCreationEnabled: this.mobileConfig.current.mobileAdvertisementCreationEnabled,
      mobileCollaborationCreationEnabled: this.mobileConfig.current.mobileCollaborationCreationEnabled,
      activeActionId: this.actionsService.contextualAction?.id || null,
      registeredActions: this.actionsService.contextualActionCount,
      backHandlers: this.mobileBack.handlerCount,
      dom: {
        videoElements: this.isBrowser ? this.document.querySelectorAll('video').length : 0,
        ionicOverlays: visibleIonicOverlays.length,
        activeIonicOverlay: activeIonicOverlay?.tagName.toLowerCase() || null,
        activeIonicOverlayParent: activeIonicOverlay?.parentElement
          ? `${activeIonicOverlay.parentElement.tagName.toLowerCase()}${activeIonicOverlay.parentElement.id ? `#${activeIonicOverlay.parentElement.id}` : ''}`
          : null,
        ionicAppRoot: appRoot ? `${appRoot.tagName.toLowerCase()}#${appRoot.id}` : null,
        ionicAppRootPointerEvents: appRoot ? getComputedStyle(appRoot).pointerEvents : null,
        conflicts: this.conflictSnapshot(),
      },
      video: this.videoService.getDiagnostics(),
      vehicleWizard: {...this.vehicleWizard, renderedStep},
      trace: this.wizardTrace.map((entry) => ({...entry, details: {...entry.details}})),
    };
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.isBrowser && !environment.production) {
      WIZARD_INPUT_EVENTS.forEach((eventName) => {
        this.document.removeEventListener?.(eventName, this.trackWizardInteractionCapture, true);
        this.document.removeEventListener?.(eventName, this.trackWizardInteractionBubble, false);
      });
      this.unbindAllSurfaces();
      this.clearPendingTaps();
      this.restoreEventMutationDiagnostics();
      IONIC_OVERLAY_LIFECYCLE_EVENTS.forEach((eventName) => this.document.removeEventListener?.(eventName, this.trackIonicLifecycle, true));
      window.removeEventListener('error', this.trackWindowError, true);
      window.removeEventListener('unhandledrejection', this.trackUnhandledRejection, true);
      delete (window as Window & {__wtgMobileExperienceDiagnostics?: MobileExperienceDiagnosticsSnapshot})
        .__wtgMobileExperienceDiagnostics;
      delete (window as Window & {__wtgMobileExperienceDiagnosticsSnapshot?: () => MobileExperienceDiagnosticsSnapshot})
        .__wtgMobileExperienceDiagnosticsSnapshot;
    }
  }

  private publish(): void {
    if (!this.initialized) {
      return;
    }
    // Keep this available to an attached debugger without sending usage or user data.
    (window as Window & {__wtgMobileExperienceDiagnostics?: MobileExperienceDiagnosticsSnapshot})
      .__wtgMobileExperienceDiagnostics = this.snapshot();
    // Safari's console keeps a reference to an older assigned object. Expose a
    // fresh function so a freeze can be inspected after it happened.
    (window as Window & {__wtgMobileExperienceDiagnosticsSnapshot?: () => MobileExperienceDiagnosticsSnapshot})
      .__wtgMobileExperienceDiagnosticsSnapshot = () => this.snapshot();
  }

  private visibleIonicOverlays(): HTMLElement[] {
    if (!this.isBrowser) {
      return [];
    }
    return [];
  }

  private renderedWizardStep(): WizardDiagnosticStep | null {
    if (!this.isBrowser) {
      return null;
    }
    return null;
  }

  /**
   * Development-only evidence for a reported unresponsive tap. It records only
   * the target's element metadata, never visible labels or user-provided data.
   */
  private readonly trackWizardInteractionCapture = (event: Event): void => this.trackWizardInteraction(event, 'document', 'capture');
  private readonly trackWizardInteractionBubble = (event: Event): void => this.trackWizardInteraction(event, 'document', 'bubble');

  private trackWizardInteraction(event: Event, surface: WizardEventSurface, phase: WizardEventPhase): void {
    const modal = this.wizardModalFor(event);
    if (!this.vehicleWizard.open || !modal) {
      return;
    }
    this.refreshWizardSurfaces();
    const path = this.eventPath(event);
    const target = path.find((entry): entry is HTMLElement => entry instanceof HTMLElement);
    // `ion-item` and `ion-button` retarget events across their Shadow DOM.
    // Checking the composed path (rather than HTMLElement.closest()) keeps the
    // diagnostic accurate for native Ionic controls.
    if (!target) {
      return;
    }
    const interactive = target.closest<HTMLElement>('ion-item, ion-button, button, ion-searchbar, input') || target;
    const targetLabel = this.elementLabel(interactive) || interactive.tagName.toLowerCase();
    const disabled = interactive.matches(':disabled, [disabled], [aria-disabled="true"]');
    if (surface === 'window' && phase === 'capture' && this.isPointerInteractionType(event.type)) {
      this.vehicleWizard = {
        ...this.vehicleWizard,
        lastInteraction: {type: event.type, target: targetLabel, disabled, at: Date.now()},
      };
    }
    const coordinates = this.eventCoordinates(event);
    const visibleOverlays = this.visibleIonicOverlays();
    const activeOverlay = visibleOverlays[visibleOverlays.length - 1];
    this.appendTrace(`dom.${event.type}`, {
      surface,
      phase,
      target: targetLabel,
      path: this.pathLabel(path),
      disabled,
      defaultPrevented: event.defaultPrevented,
      cancelBubble: event.cancelBubble,
      cancelable: event.cancelable,
      eventPhase: event.eventPhase,
      pointerType: this.pointerType(event),
      pointerId: this.pointerId(event),
      x: coordinates.x,
      y: coordinates.y,
      elementAtPoint: this.elementAtPoint(coordinates.x, coordinates.y),
      activeElement: this.elementLabel(this.document.activeElement as HTMLElement | null),
      targetPointerEvents: this.computedStyle(interactive, 'pointerEvents'),
      targetTouchAction: this.computedStyle(interactive, 'touchAction'),
      modalPointerEvents: this.computedStyle(modal, 'pointerEvents'),
      activeOverlay: activeOverlay?.tagName.toLowerCase() || null,
      overlayCount: visibleOverlays.length,
      step: this.vehicleWizard.step,
      renderedStep: this.renderedWizardStep(),
      isTrusted: event.isTrusted,
    });
    if (surface === 'window' && phase === 'bubble') {
      this.trackTapCompletion(event, targetLabel, coordinates);
      this.publish();
    }
  }

  private isPointerInteractionType(type: string): type is WizardPointerInteractionType {
    return type === 'pointerdown' || type === 'pointerup' || type === 'click';
  }

  private pointerType(event: Event): string | null {
    const pointerEvent = event as PointerEvent;
    if (pointerEvent.pointerType) {
      return pointerEvent.pointerType;
    }
    return event.type.startsWith('touch') ? 'touch' : event.type.startsWith('mouse') ? 'mouse' : null;
  }

  private pointerId(event: Event): number | null {
    const pointerId = (event as PointerEvent).pointerId;
    return Number.isFinite(pointerId) ? pointerId : null;
  }

  private eventCoordinates(event: Event): {x: number | null; y: number | null} {
    const pointer = event as PointerEvent;
    if (Number.isFinite(pointer.clientX) && Number.isFinite(pointer.clientY)) {
      return {x: pointer.clientX, y: pointer.clientY};
    }
    const touch = (event as TouchEvent).changedTouches?.[0] || (event as TouchEvent).touches?.[0];
    return touch ? {x: touch.clientX, y: touch.clientY} : {x: null, y: null};
  }

  private bindSurface(surface: WizardEventSurface, target: EventTarget | null): void {
    if (!target || this.surfaceReleases.has(surface)) {
      return;
    }
    const releases: Array<() => void> = [];
    WIZARD_INPUT_EVENTS.forEach((eventName) => {
      const capture = (event: Event) => this.trackWizardInteraction(event, surface, 'capture');
      const bubble = (event: Event) => this.trackWizardInteraction(event, surface, 'bubble');
      target.addEventListener(eventName, capture, true);
      target.addEventListener(eventName, bubble, false);
      releases.push(() => target.removeEventListener(eventName, capture, true));
      releases.push(() => target.removeEventListener(eventName, bubble, false));
    });
    this.surfaceReleases.set(surface, releases);
  }

  private refreshWizardSurfaces(): void {
    if (!this.isBrowser) {
      return;
    }
    this.bindSurface('app', this.document.getElementById('app-root-content'));
  }

  private unbindAllSurfaces(): void {
    this.surfaceReleases.forEach((releases) => releases.forEach((release) => release()));
    this.surfaceReleases.clear();
  }

  private wizardModalFor(event: Event): HTMLElement | null {
    const path = this.eventPath(event);
    return null;
  }

  private eventPath(event: Event): EventTarget[] {
    return typeof event.composedPath === 'function' ? event.composedPath() : event.target ? [event.target] : [];
  }

  private elementLabel(element: HTMLElement | null): string | null {
    if (!element) {
      return null;
    }
    const classes = typeof element.className === 'string'
      ? element.className.split(' ').filter(Boolean).slice(0, 4).join('.')
      : '';
    return `${element.tagName.toLowerCase()}${element.id ? `#${element.id}` : ''}${classes ? `.${classes}` : ''}`;
  }

  private pathLabel(path: EventTarget[]): string | null {
    const labels = path
      .filter((entry): entry is HTMLElement => entry instanceof HTMLElement)
      .slice(0, 8)
      .map((entry) => this.elementLabel(entry))
      .filter((label): label is string => !!label);
    return labels.length ? labels.join(' > ') : null;
  }

  private computedStyle(element: HTMLElement | null, property: 'pointerEvents' | 'touchAction'): string | null {
    if (!element || !this.isBrowser) {
      return null;
    }
    return getComputedStyle(element)[property] || null;
  }

  private elementAtPoint(x: number | null, y: number | null): string | null {
    if (x === null || y === null || !this.isBrowser) {
      return null;
    }
    return this.elementLabel(this.document.elementFromPoint?.(x, y) as HTMLElement | null);
  }

  private trackTapCompletion(event: Event, target: string, coordinates: {x: number | null; y: number | null}): void {
    const pointerId = this.pointerId(event);
    if (event.type === 'pointerdown' && pointerId !== null) {
      this.clearPendingTap(pointerId);
      return;
    }
    if (event.type === 'pointercancel' && pointerId !== null) {
      this.clearPendingTap(pointerId);
      this.traceVehicleWizard('dom.tap.cancelled', {pointerId, target});
      return;
    }
    if (event.type === 'pointerup' && pointerId !== null) {
      const tap: WizardPendingTap = {
        pointerId,
        target,
        x: coordinates.x,
        y: coordinates.y,
        at: Date.now(),
        clickSeen: false,
        timeoutId: null,
      };
      this.pendingTaps.set(pointerId, tap);
      Promise.resolve().then(() => this.traceTapCheckpoint(tap, 'microtask'));
      window.requestAnimationFrame(() => this.traceTapCheckpoint(tap, 'animation-frame'));
      tap.timeoutId = window.setTimeout(() => this.traceTapCheckpoint(tap, 'timeout'));
      return;
    }
    if (event.type === 'click') {
      const now = Date.now();
      this.pendingTaps.forEach((tap) => {
        if (!tap.clickSeen && now - tap.at <= 750 && (tap.target === target || this.pendingTaps.size === 1)) {
          tap.clickSeen = true;
          this.traceVehicleWizard('dom.tap.click-observed', {pointerId: tap.pointerId, target, delayMs: now - tap.at});
          this.clearPendingTap(tap.pointerId);
        }
      });
    }
  }

  private traceTapCheckpoint(tap: WizardPendingTap, checkpoint: 'microtask' | 'animation-frame' | 'timeout'): void {
    if (tap.clickSeen || this.pendingTaps.get(tap.pointerId) !== tap) {
      return;
    }
    this.traceVehicleWizard(checkpoint === 'timeout' ? 'dom.tap.click-missing' : 'dom.tap.awaiting-click', {
      checkpoint,
      pointerId: tap.pointerId,
      target: tap.target,
      elapsedMs: Date.now() - tap.at,
      elementAtPoint: this.elementAtPoint(tap.x, tap.y),
    });
    if (checkpoint === 'timeout') {
      this.clearPendingTap(tap.pointerId);
    }
  }

  private clearPendingTap(pointerId: number): void {
    const tap = this.pendingTaps.get(pointerId);
    if (tap && tap.timeoutId !== null) {
      window.clearTimeout(tap.timeoutId);
    }
    this.pendingTaps.delete(pointerId);
  }

  private clearPendingTaps(): void {
    Array.from(this.pendingTaps.keys()).forEach((pointerId) => this.clearPendingTap(pointerId));
  }

  private readonly trackIonicLifecycle = (event: Event): void => {
    if (!this.isIonicOverlay(event.target)) {
      return;
    }
    // Lifecycle is the earliest reliable point at which Ionic has connected an
    // overlay to the DOM. Bind its hosts before the first user interaction.
    this.refreshWizardSurfaces();
    if (!this.vehicleWizard.open) {
      return;
    }
    const target = event.target as HTMLElement;
    this.traceVehicleWizard(`ionic.${event.type}`, {
      overlay: target.tagName.toLowerCase(),
      id: target.id || null,
      overlayCount: this.visibleIonicOverlays().length,
    });
  };

  private readonly trackWindowError = (event: ErrorEvent): void => {
    if (!this.vehicleWizard.open) {
      return;
    }
    const error = event.error as {name?: unknown; message?: unknown; stack?: unknown} | undefined;
    this.traceVehicleWizard('window.error', {
      name: this.sanitizeErrorText(error?.name || 'Error'),
      message: this.sanitizeErrorText(error?.message || event.message || null),
      stack: this.stackPreview(error?.stack),
    });
  };

  private readonly trackUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (!this.vehicleWizard.open) {
      return;
    }
    const reason = event.reason as {name?: unknown; message?: unknown; stack?: unknown} | undefined;
    this.traceVehicleWizard('window.unhandledrejection', {
      name: this.sanitizeErrorText(reason?.name || 'UnhandledRejection'),
      message: this.sanitizeErrorText(reason?.message || reason || null),
      stack: this.stackPreview(reason?.stack),
    });
  };

  private installEventMutationDiagnostics(): void {
    const prototype = Event.prototype as unknown as Record<WizardEventMutation, Function>;
    (['preventDefault', 'stopPropagation', 'stopImmediatePropagation'] as WizardEventMutation[]).forEach((method) => {
      const original = prototype[method];
      if (typeof original !== 'function') {
        return;
      }
      const diagnostics = this;
      const patched = function(this: Event, ...args: unknown[]): unknown {
        // Capture before calling the native method: this leaves the actual
        // listener that requested the mutation in the short stack preview.
        const stack = new Error().stack;
        const result = original.apply(this, args);
        diagnostics.trackEventMutation(method, this, stack);
        return result;
      };
      try {
        prototype[method] = patched;
        this.patchedEventMethods.set(method, {original, patched});
      } catch {
        // WebKit may expose a non-writable prototype in some embedding modes.
      }
    });
  }

  private restoreEventMutationDiagnostics(): void {
    const prototype = Event.prototype as unknown as Record<WizardEventMutation, Function>;
    this.patchedEventMethods.forEach(({original, patched}, method) => {
      try {
        if (prototype[method] === patched) {
          prototype[method] = original;
        }
      } catch {
        // Do not overwrite a later framework patch if WebKit rejects restoration.
      }
    });
    this.patchedEventMethods.clear();
  }

  private trackEventMutation(method: WizardEventMutation, event: Event, stack: string | undefined): void {
    const modal = this.wizardModalFor(event);
    if (!this.vehicleWizard.open || !modal) {
      return;
    }
    const path = this.eventPath(event);
    const target = path.find((entry): entry is HTMLElement => entry instanceof HTMLElement) || null;
    this.appendTrace(`dom.${method}`, {
      eventType: event.type,
      target: this.elementLabel(target),
      path: this.pathLabel(path),
      defaultPrevented: event.defaultPrevented,
      cancelBubble: event.cancelBubble,
      stack: this.stackPreview(stack),
    });
  }

  private isIonicOverlay(target: EventTarget | null): target is HTMLElement {
    return false;
  }

  private conflictSnapshot(): MobileExperienceDiagnosticsSnapshot['dom']['conflicts'] {
    if (!this.isBrowser) {
      return {
        primeMasks: 0, bottomSheets: 0, tutorialActive: false, htmlTouchAction: null,
        htmlClasses: null, bodyClasses: null, htmlOverflow: null, htmlPosition: null,
        bodyTouchAction: null, bodyOverflow: null, bodyPosition: null, bodyScrollLocked: false,
      };
    }
    const html = this.document.documentElement;
    const body = this.document.body;
    const htmlStyle = html ? getComputedStyle(html) : null;
    const bodyStyle = body ? getComputedStyle(body) : null;
    return {
      primeMasks: this.document.querySelectorAll('.p-component-overlay, .p-dialog-mask, .p-sidebar-mask, .p-galleria-mask').length,
      bottomSheets: this.document.querySelectorAll('.cupertino-pane, app-bottom-sheet').length,
      tutorialActive: !!this.document.querySelector('.driver-popover, .driver-overlay'),
      htmlClasses: this.classNames(html),
      bodyClasses: this.classNames(body),
      htmlTouchAction: htmlStyle?.touchAction || null,
      htmlOverflow: htmlStyle?.overflow || null,
      htmlPosition: htmlStyle?.position || null,
      bodyTouchAction: bodyStyle?.touchAction || null,
      bodyOverflow: bodyStyle?.overflow || null,
      bodyPosition: bodyStyle?.position || null,
      bodyScrollLocked: !!body && (
        body.classList.contains('backdrop-no-scroll')
        || body.classList.contains('mobile-vehicle-wizard-open')
        || body.classList.contains('ion-no-scroll')
        || html?.classList.contains('ion-no-scroll')
      ),
    };
  }

  private classNames(element: Element | null): string | null {
    if (!element) {
      return null;
    }
    const names = Array.from(element.classList).slice(0, 20);
    return names.length ? names.join(' ') : null;
  }

  private appendTrace(event: string, details: Record<string, boolean | number | string | null>): void {
    const entry = {at: Date.now(), event, details};
    this.wizardTrace.push(entry);
    if (this.wizardTrace.length > WIZARD_TRACE_LIMIT) {
      this.wizardTrace.splice(0, this.wizardTrace.length - WIZARD_TRACE_LIMIT);
    }
    console.debug('[WTG wizard]', entry);
  }

  private sanitizeErrorText(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    return String(value)
      .replace(/https?:\/\/[^\s)]+/g, (url) => {
        try {
          const parsed = new URL(url);
          return `${parsed.origin}${parsed.pathname}`;
        } catch {
          return '[url]';
        }
      })
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 320) || null;
  }

  private stackPreview(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    return this.sanitizeErrorText(value.split('\n').slice(0, 3).join('\n'));
  }
}
